import { store } from "../redux/store";
import { currentUserVerificationData } from "../redux/Actions/ClientActions";

/**
 * Utility functions for verification data management
 */

// Cache to track last refresh times per user
const refreshCache = new Map<string | number, number>();
const REFRESH_COOLDOWN = 5000; // 5 seconds cooldown between refreshes

/**
 * Refresh verification data in the Redux store
 * Can be called after successful verification actions
 * Includes cooldown to prevent excessive API calls
 */
export const refreshVerificationData = async (userId: number | string, force: boolean = false): Promise<void> => {
    // Validate userId
    console.log("userId", userId);
    if (!userId || userId === undefined || userId === null) {
        console.warn("refreshVerificationData called with invalid userId:", userId);
        return;
    }
    
    const now = Date.now();
    const lastRefresh = refreshCache.get(userId);
    
    // Check cooldown unless forced
    if (!force && lastRefresh && (now - lastRefresh) < REFRESH_COOLDOWN) {
        console.log("Verification data refresh skipped due to cooldown");
        return;
    }
    
    try {
        await store.dispatch(currentUserVerificationData({ id: userId }));
        refreshCache.set(userId, now);
    } catch (error) {
        console.error("Failed to refresh verification data:", error);
        throw error;
    }
};

/**
 * Refresh verification data if user is authenticated
 * Automatically gets user ID from the store
 */
export const refreshVerificationDataIfAuthenticated = async (): Promise<void> => {
    const state = store.getState();
    const user = state.auth?.user;
    console.log("refreshVerificationDataIfAuthenticated - user:", user);
    
    if (!user?.id) {
        console.warn("No authenticated user found for verification refresh");
        return;
    }
    
    // Handle both string and number user IDs
    let userId: number | string = user.id;
    
    // If it's a string, try to convert to number, but keep as string if it's not a valid number
    if (typeof userId === "string") {
        const numericId = Number(userId);
        if (!isNaN(numericId) && numericId > 0) {
            userId = numericId;
        }
        // If it's a string that can't be converted to a valid number, keep it as string
    }
    
    if (!userId) {
        console.warn("Invalid user ID for verification refresh:", user.id);
        return;
    }
    
    await refreshVerificationData(userId);
};

/**
 * Create a callback that refreshes verification data after an async operation
 */
export const createVerificationRefreshCallback = (userId: number | string) => {
    return async (operation: () => Promise<any>) => {
        try {
            const result = await operation();
            console.log("result", result, userId);
            // Refresh verification data after successful operation
            await refreshVerificationData(userId);
            return result;
        } catch (error) {
            throw error;
        }
    };
};

export default {
    refreshVerificationData,
    refreshVerificationDataIfAuthenticated,
    createVerificationRefreshCallback,
};
